/* eslint-disable @typescript-eslint/ban-ts-comment */

import { Formik, Form } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { getAddressFromUpdateAddressParams } from "@/core/utils/address";
import { parseMaskedDollarInput } from "@/core/utils/money";
import { getCustomerNameOrPhone } from "@/customer/utils";
import {
  COLUMN_TO_UPDATE_MODAL_INFO,
  REFERRAL_COLUMN,
  REFERRAL_FIELD,
  REFERRAL_FIELD_TO_UPDATE_FIELD,
} from "@/referrals/constants";
import type {
  IReferral,
  ReferralColumn,
  ReferralUpdateParams,
  ReferralUpdateFormValues,
} from "@/referrals/referral";
import { BaseContent } from "./base-content";

interface UpdateReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referral?: IReferral | null;
  targetColumn?: ReferralColumn | null;
  onSubmit: (values: ReferralUpdateParams) => void;
}

const COLUMN_TO_UPDATE_LABEL = {
  [REFERRAL_COLUMN.new]: "Revert to new",
  [REFERRAL_COLUMN.inProgress]: "Move to in progress",
  [REFERRAL_COLUMN.scheduled]: "Move to scheduled",
  [REFERRAL_COLUMN.closed]: "Move to closed",
} as const;

const getValidationSchema = (targetColumn: ReferralColumn) => {
  const modalInfo = COLUMN_TO_UPDATE_MODAL_INFO[targetColumn];
  if (!modalInfo) return Yup.object();

  const schema: Record<string, Yup.AnySchema> = {};
  const allFields = [
    ...modalInfo.constantFormFields,
    ...modalInfo.ifNeededFormFields,
  ].map((field) => REFERRAL_FIELD_TO_UPDATE_FIELD[field]);

  allFields.forEach((field) => {
    const isRequired = modalInfo.requiredFields
      .map((f) => REFERRAL_FIELD_TO_UPDATE_FIELD[f])
      .includes(field as never);

    if (field === REFERRAL_FIELD.address) {
      schema[field] = Yup.object()
        .shape({
          line_one: Yup.string(),
          line_two: Yup.string(),
          city: Yup.string(),
          state: Yup.string(),
          zip_code: Yup.string(),
        })
        .nullable()
        .test(
          "all-or-none",
          "Please complete all address fields (Address, City, State, and ZIP) or leave them all empty",
          (value) => {
            if (!value) return true;
            const fields: (keyof typeof value)[] = [
              "line_one",
              "city",
              "state",
              "zip_code",
            ];
            const filledFields = fields.filter((f) => value[f]);
            return (
              filledFields.length === 0 || filledFields.length === fields.length
            );
          },
        );
    } else {
      schema[field] = Yup.string();
      if (isRequired) {
        schema[field] = schema[field].required(`${field} is required`);
      } else {
        if (field === REFERRAL_FIELD.appointment_date) {
          schema[field] = Yup.date().nullable();
        } else {
          schema[field] = schema[field].nullable();
        }
      }
    }
  });

  return Yup.object().shape(schema);
};

const UpdateReferralModal = ({
  open,
  onOpenChange,
  referral,
  targetColumn,
  onSubmit,
}: UpdateReferralModalProps) => {
  const validationSchema = useMemo(
    () => (targetColumn ? getValidationSchema(targetColumn) : Yup.object()),
    [targetColumn],
  );

  if (!targetColumn || !referral) return null;

  const modalInfo = COLUMN_TO_UPDATE_MODAL_INFO[targetColumn];
  if (!modalInfo) return null;

  const initialValues: Partial<ReferralUpdateFormValues> = {
    status: modalInfo.status,
  };

  const initializeFields = [
    ...modalInfo.constantFormFields,
    ...modalInfo.requiredFields,
  ];

  initializeFields.forEach((field) => {
    switch (field) {
      case REFERRAL_FIELD.deal_size:
        // Convert number to string for MoneyInput
        initialValues.deal_size = referral.deal_size
          ? referral.deal_size.toString()
          : "";
        break;
      default:
        // @ts-expect-error
        initialValues[field as keyof ReferralUpdateFormValues] =
          referral[field] || "";
    }
  });

  modalInfo.ifNeededFormFields.forEach((field) => {
    if (!referral[field]) {
      // @ts-expect-error
      initialValues[field as keyof ReferralUpdateParams] = "";
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] min-h-0 flex-col overflow-hidden">
        <DialogDescription className="sr-only">
          {modalInfo.title}
        </DialogDescription>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnMount
          onSubmit={(values, { setSubmitting }) => {
            // Convert deal_size back to number for API (same pattern as table-sidebar)
            const parsedDealSize = parseMaskedDollarInput(
              values.deal_size || "",
            );

            const updateParams: Partial<ReferralUpdateParams> = {
              ...values,
              address: getAddressFromUpdateAddressParams(values.address),
              deal_size:
                parsedDealSize !== null && parsedDealSize > 0
                  ? parsedDealSize
                  : null,
            };
            onSubmit(updateParams);
            setSubmitting(false);
          }}
        >
          {({ isSubmitting, isValid }) => (
            <Form className="grid max-h-[80vh] grid-rows-[auto_1fr_auto]">
              <DialogHeader>
                <DialogTitle>{modalInfo.title}</DialogTitle>
              </DialogHeader>

              <div className="grid min-h-0 grid-rows-[auto_1fr] gap-6 py-4">
                <Stack align="start" gap={2}>
                  <Text variant="label" tone="muted">
                    Referral
                  </Text>
                  <Text variant="title">
                    {getCustomerNameOrPhone(referral.potential_customer)}
                  </Text>
                </Stack>
                <div className="-mx-4 min-h-0 overflow-y-auto px-4">
                  <BaseContent referral={referral} modalInfo={modalInfo} />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                >
                  {COLUMN_TO_UPDATE_LABEL[targetColumn]}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateReferralModal;
